import { computed, inject, Injectable } from '@angular/core';
import { ProFormaTranslationPaths } from '@rx-applications/clientsite/shared/utils';

import { differenceInDays, format, parse } from 'date-fns';
import html2canvas from 'html2canvas';

import { DAY_MONTH_YEAR, MONTH_DAY_YEAR_WITH_TIME } from '@rx-applications/shared/utils';

import { ConfirmationGuest, ConfirmationRoom, ReservationViewResponse } from '@app/layout-app/shared/models';

import { UtilityService } from './utility.service';
import { BrandingManagerService } from '@rx-applications/shared/branding/data-access';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let BytescoutPDF: any;
}

@Injectable({ providedIn: 'root' })
export class ReservationService {
  websiteId: number;
  companyName: string;

  logoImage =inject(BrandingManagerService).getBrandAccess().getInvoiceLogoUrl()

  constructor(private utility: UtilityService) {}

  public downloadPdf(
    reservation: ReservationViewResponse,
    translations: { [key in ProFormaTranslationPaths]: string },
    websiteId: number,
    companyName: string
  ) {
    html2canvas(document.body).then((canvas) => document.body.appendChild(canvas));
    const pdf = new BytescoutPDF();
    this.websiteId = websiteId;

    pdf.JPEGQuality = 1;
    // set page size
    pdf.pageSetSize(BytescoutPDF.A4);

    // set portrait page orientation
    pdf.pageSetOrientation(BytescoutPDF.PORTRAIT);

    // add new page
    pdf.pageAdd();

    // add logo
    pdf.imageLoadFromUrl(this.logoImage);
    pdf.imagePlaceSetSize(422, 0, 0, 165, 64);

    // set font name
    pdf.fontSetName('Times-Roman');

    // add requisites
    pdf.fontSetSize(10);
    pdf.fontSetColor(155, 155, 155);
    pdf.textAdd(50, 30, 'Roomex');
    pdf.textAdd(50, 40, 'Unit D10, Nutgrove Office Park');
    pdf.textAdd(50, 50, 'Rathfarnham, Dublin 14.');

    pdf.textSetBox(50, 70, 520, 50);
    pdf.fontSetName('Helvetica');
    pdf.fontSetSize(9);
    pdf.textSetAlign(BytescoutPDF.RIGHT);
    pdf.fontSetColor(126, 151, 173);
    pdf.textAddToBox(
      `${translations[ProFormaTranslationPaths.Date]}:   ${this.formatDate(
        reservation.issued
      )}`
    );
    pdf.textAddToBox(
      `${translations[ProFormaTranslationPaths.RefNumber]}:   ${
        reservation.reference
      }`
    );
    pdf.textAddToBox(
      `${
        translations[ProFormaTranslationPaths.AccNumber]
      }:             ${websiteId}`
    );

    // client fields
    const orderNumberField = this.searchClientFields(reservation);
    if (orderNumberField) {
      pdf.textAddToBox(
        `${
          translations[ProFormaTranslationPaths.PurchaseOrderNum]
        }:             ${
          orderNumberField
            ? orderNumberField.value
            : `${translations[ProFormaTranslationPaths.None]}`
        }`
      );
    }

    pdf.textSetBox(50, 135, 520, 20);
    pdf.textSetAlign(BytescoutPDF.CENTER);
    pdf.fontSetSize(16);
    pdf.textAddToBox(`${translations[ProFormaTranslationPaths.Title]}`);
    pdf.graphicsSetColor(126, 151, 173);
    pdf.graphicsDrawLine(50, 160, 570, 160);

    // comany address
    pdf.fontSetName('Times-Roman');
    pdf.fontSetColor(0, 0, 0);
    pdf.fontSetSize(11);
    if (this.isBelfor()) {
      // Belfor to remain as hardcoded address
      pdf.textAdd(50, 185, 'Belfor UK Ltd.');
      pdf.textAdd(50, 197, 'Belfor House');
      pdf.textAdd(50, 209, 'Trinity Road');
      pdf.textAdd(50, 221, 'Picadilly');
      pdf.textAdd(50, 233, 'Tamworth');
    } else {
      pdf.textAdd(50, 185, `${companyName}`);
    }
    pdf.textSetBoxPadding(3, 3, 3, 3);

    // draw table header
    pdf.graphicsSetColor(0, 0, 0);
    pdf.graphicsDrawRectangle(50, 250, 520, 160);
    pdf.graphicsSetColor(0, 0, 0);
    pdf.fontSetColor(255, 255, 255);
    pdf.graphicsDrawRectangleFill(50, 250, 520, 20);
    pdf.textSetAlign(BytescoutPDF.CENTER);
    // add 'First' column

    if (this.isBelfor()) {
      // Belfor to retain these fields
      pdf.graphicsDrawLine(300, 250, 300, 410);
      // add 'Net' column
      pdf.textSetBox(300, 250, 80, 20);
      pdf.textAddToBox(`${translations[ProFormaTranslationPaths.Net]}`);
      pdf.graphicsDrawLine(380, 250, 380, 410);
      // add 'Vat' column
      pdf.textSetBox(380, 250, 100, 20);
      pdf.textAddToBox(`${translations[ProFormaTranslationPaths.Vat]}`);
      pdf.graphicsDrawLine(480, 250, 480, 410);
    }

    // add 'Total' column
    pdf.graphicsDrawLine(480, 250, 480, 410);
    pdf.textSetBox(480, 250, 90, 20);
    pdf.textAddToBox(`${translations[ProFormaTranslationPaths.Total]}`);
    pdf.fontSetColor(0, 0, 0);
    pdf.textSetAlign(BytescoutPDF.LEFT);
    // First column and subcolumns

    const addressLine1 = reservation.address.split(',');
    const addressLine2 =
      addressLine1.length > 4
        ? addressLine1.splice(0, Math.ceil(addressLine1.length / 2))
        : [''];

    pdf.textSetBox(50, 270, 70, 80);
    pdf.fontSetSize(8);
    pdf.textAddToBox(`${translations[ProFormaTranslationPaths.Dates]}:`);
    pdf.textAddToBox(`${translations[ProFormaTranslationPaths.Hotel]}:`);
    if (reservation.address !== '') {
      pdf.textAddToBox(' ');
      if (reservation.address.split(',').length > 4) {
        pdf.textAddToBox(' ');
      }
    }
    pdf.textAddToBox(
      `${translations[ProFormaTranslationPaths.NumberOfRooms]}:`
    );
    pdf.textAddToBox(`${translations[ProFormaTranslationPaths.RoomNights]}:`);
    pdf.textAddToBox(`${translations[ProFormaTranslationPaths.Passengers]}:`);
    pdf.textSetBox(110, 270, 230, 140);
    const checkIn = this.parseDate(reservation.checkIn);
    const checkOut = this.parseDate(reservation.checkOut);
    pdf.textAddToBox(
      `${this.formatDate(reservation.checkIn)} - ${this.formatDate(
        reservation.checkOut
      )}`
    );
    pdf.textAddToBox(reservation.hotelName);

    if (reservation.address !== '') {
      if (reservation.address.split(',').length > 4) {
        pdf.textAddToBox(addressLine2.join(','));
        pdf.textAddToBox(addressLine1.join(','));
      } else {
        pdf.textAddToBox(addressLine1.join(','));
      }
    }

    pdf.textAddToBox(reservation.rooms.length.toString());
    pdf.textAddToBox(
      (
        differenceInDays(checkOut, checkIn) * reservation.rooms.length
      ).toString()
    );
    reservation.rooms.forEach((room: ConfirmationRoom) => {
      room.adults.forEach((adult: ConfirmationGuest) => {
        pdf.textAddToBox(`${adult.firstName} ${adult.lastName}`);
      });
    });

    const net = (reservation.total - reservation.reservationUkVat)
      .toFixed(2)
      .toString();
    const vat = reservation.reservationUkVat.toFixed(2).toString();
    const total = reservation.total.toFixed(2).toString();

    if (this.isBelfor()) {
      // Belfor to retain these fields
      // NET
      pdf.textSetAlign(BytescoutPDF.CENTER);
      pdf.textSetBox(300, 270, 80, 80);
      pdf.textAddToBox(' ');
      pdf.textAddToBox(`${net} ${reservation.currency}`);
      // VAT
      pdf.textSetBox(380, 270, 100, 80);
      pdf.textAddToBox(' ');
      pdf.textAddToBox(`${vat} ${reservation.currency}`);
    }

    // TOTAL
    pdf.textSetBox(480, 270, 90, 80);
    pdf.textAddToBox(' ');
    pdf.textSetAlign(BytescoutPDF.CENTER);
    pdf.textAddToBox(`${total} ${reservation.currency}`);

    // Bottom totals
    pdf.fontSetName('Helvetica');
    pdf.fontSetSize(9);
    pdf.fontSetColor(87, 113, 136);
    pdf.textSetBox(270, 470, 150, 100);
    pdf.textSetAlign(BytescoutPDF.LEFT);

    if (this.isBelfor()) {
      // Belfor to retain these fields
      pdf.textAddToBox(`${translations[ProFormaTranslationPaths.SubTotal]}`);
      pdf.textAddToBox(' ');
      pdf.textAddToBox(`${translations[ProFormaTranslationPaths.Vat]}`);
      pdf.textAddToBox(' ');
    }

    pdf.fontSetStyle(true, false, false);
    pdf.textAddToBox(`${translations[ProFormaTranslationPaths.TotalDue]}`);
    pdf.textSetBox(420, 470, 150, 100);
    pdf.textSetAlign(BytescoutPDF.RIGHT);

    if (this.isBelfor()) {
      // Belfor retain these fields
      pdf.fontSetStyle(false, false, false);
      pdf.textAddToBox(`${net} ${reservation.currency}`);
      pdf.textAddToBox(' ');
      pdf.textAddToBox(`${vat} ${reservation.currency}`);
      pdf.textAddToBox(' ');
      pdf.graphicsSetColor(222, 222, 222);
      pdf.graphicsDrawLine(270, 490, 570, 490);
      pdf.graphicsDrawLine(270, 510, 570, 510);
    }

    pdf.fontSetStyle(true, false, false);
    pdf.textAddToBox(`${total} ${reservation.currency}`);

    pdf.graphicsSetColor(222, 222, 222);
    pdf.graphicsDrawLine(270, 470, 570, 470);

    pdf.onload(() => {
      const blob = this.utility.b64toBlob(
        pdf.getBase64Text(),
        'application/pdf',
        512
      );
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.download = `invoice-${reservation.guid}.pdf`;
      anchor.href = blobUrl;
      anchor.click();
    });
  }

  private isBelfor(): boolean {
    return this.websiteId === 2259;
  }

  private searchClientFields(reservation: ReservationViewResponse) {
    return (
      reservation.clientFields.find(
        (field: { name: string; value: string; label?: string }) =>
          field.name === 'Purchase Order' || field.label === 'Purchase Order'
      ) || null
    );
  }

  private formatDate(date: string) {
    return format(this.parseDate(date), DAY_MONTH_YEAR);
  }

  private parseDate(date: string) {
    return parse(date, MONTH_DAY_YEAR_WITH_TIME, new Date());
  }
}
